import { IEntityFactoryAdapter } from './IEntityFactoryAdapter';
import { ContentRegistry, loadContentRegistry } from '../data';
import { createLogger } from '../utils/Logger';
import { usesFallbackTexture, VisualTextureResolver } from '../utils/VisualTextureResolver';
import { applyCharacterDisplaySize } from '../utils/CharacterDisplaySizing';

/**
 * Адаптер для фабрики сущностей Phaser, реализующий интерфейс IEntityFactoryAdapter
 * Позволяет использовать Phaser-специфичную фабрику сущностей через абстрактный интерфейс
 */
export class PhaserEntityFactoryAdapter implements IEntityFactoryAdapter {
  private scene: Phaser.Scene;
  private contentRegistry: ContentRegistry;
  private readonly logger = createLogger('PhaserEntityFactoryAdapter');

  constructor(
    scene: Phaser.Scene,
    contentRegistry: ContentRegistry = loadContentRegistry(),
    private readonly visualTextureResolver?: VisualTextureResolver
  ) {
    this.scene = scene;
    this.contentRegistry = contentRegistry;
  }

  /**
   * Создает врага в сцене
   * @param x - координата X
   * @param y - координата Y
   * @param enemyId - ID типа врага
   */
  createEnemy(x: number, y: number, enemyId: string): any {
    // Look up config or default to basic 'enemy'
    // Map legacy 'enemy' to 'basic_enemy' if needed
    const key = enemyId === 'enemy' ? 'basic_enemy' : enemyId;

    this.logger.debug('createEnemy:lookup', { enemyId, resolvedEnemyId: key });
    const overrides = this.contentRegistry.getEnemy(key);
    const defaultConfig = this.contentRegistry.getEnemy('basic_enemy');
    const config = overrides || defaultConfig;

    if (!config) {
      this.logger.error('Enemy config not found and default is missing', { enemyId, defaultEnemyId: 'basic_enemy' });
      return null;
    }

    const texture = this.visualTextureResolver?.resolve(config.sprite, 'enemy') ?? {
      requestedKey: config.sprite,
      resolvedKey: config.sprite,
      source: 'direct' as const,
      fallbackUsed: false
    };
    const enemy = this.scene.physics.add.sprite(x, y, texture.resolvedKey);

    if (enemy) {
      const displaySize = applyCharacterDisplaySize(enemy, texture.resolvedKey, 'enemy', config.scale || 1);
      if (config.color && usesFallbackTexture(texture)) {
        enemy.setTint(config.color);
      } else {
        enemy.clearTint?.();
      }

      // Access stats from stats object
      enemy.setData('health', config.stats.hp);
      enemy.setData('id', config.id);
      enemy.setData('maxHealth', config.stats.hp);
      enemy.setData('speed', config.stats.speed);
      enemy.setData('damage', config.stats.damage);
      enemy.setData('xpReward', config.stats.xpReward);
      enemy.setData('visualTextureKey', texture.resolvedKey);
      enemy.setData('contentSpriteKey', config.sprite);
      enemy.setData('visualDisplayWidth', displaySize.width);
      enemy.setData('visualDisplayHeight', displaySize.height);
    }

    this.logger.info('createEnemy:complete', {
      enemyId,
      resolvedEnemyId: config.id,
      hp: config.stats.hp,
      speed: config.stats.speed,
      visualTextureKey: texture.resolvedKey,
      visualSource: texture.source
    });

    return enemy;
  }

  /**
   * Создает игрока в сцене
   * @param x - координата X
   * @param y - координата Y
   */
  createPlayer(x: number, y: number): any {
    // Возвращаем объект, который будет использоваться игроком
    const texture = this.visualTextureResolver?.resolve('player_default', 'player') ?? {
      requestedKey: 'player_default',
      resolvedKey: 'player',
      source: 'direct' as const,
      fallbackUsed: false
    };
    const player = this.scene.physics.add.sprite(x, y, texture.resolvedKey);
    const displaySize = applyCharacterDisplaySize(player, texture.resolvedKey, 'player');
    player.setData?.('visualTextureKey', texture.resolvedKey);
    player.setData?.('visualDisplayWidth', displaySize.width);
    player.setData?.('visualDisplayHeight', displaySize.height);
    this.logger.info('createPlayer:complete', {
      visualTextureKey: texture.resolvedKey,
      visualSource: texture.source
    });
    // Устанавливаем свойства игрока
    if (player.body) {
      // (player.body as Phaser.Physics.Arcade.Body).setCollideWorldBounds(true);
    }
    return player;
  }

  /**
   * Создает снаряд в сцене
   * @param x - координата X
   * @param y - координата Y
   * @param type - тип снаряда
   */
  createProjectile(x: number, y: number, type: string): any {
    // Возвращаем объект, который будет использоваться снарядом
    const projectile = this.scene.physics.add.sprite(x, y, type);
    return projectile;
  }
}
